import { useState, useEffect, useCallback } from 'react';
import { getUserLocation, UserLocation } from '../services/api';
import { logger } from '../utils/logger';

const SESSION_STORAGE_KEY = 'raisa_user_location';

/**
 * Hook to get user's location from IP geolocation.
 * Caches result in sessionStorage to avoid repeated API calls.
 * 
 * Returns:
 * - location: UserLocation | null
 * - loading: boolean
 * - error: Error | null
 * - refetch: () => void - Force refetch (ignores cache)
 */
export function useUserLocation() {
    const [location, setLocation] = useState<UserLocation | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchLocation = useCallback(async (ignoreCache = false) => {
        // Check sessionStorage cache first
        if (!ignoreCache) {
            try {
                const cached = sessionStorage.getItem(SESSION_STORAGE_KEY);
                if (cached) {
                    const parsed = JSON.parse(cached);
                    // Cache is valid for 1 hour
                    if (parsed.timestamp && Date.now() - parsed.timestamp < 3600000) {
                        setLocation(parsed.location);
                        setLoading(false);
                        return parsed.location;
                    }
                }
            } catch {
                // Invalid cache, continue to fetch
            }
        }

        setLoading(true);
        try {
            const loc = await getUserLocation();
            setLocation(loc);
            setError(null);

            // Cache in sessionStorage
            try {
                sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
                    location: loc,
                    timestamp: Date.now(),
                }));
            } catch {
                // sessionStorage might be full or disabled
            }

            return loc;
        } catch (err) {
            logger.error('Failed to fetch user location:', err);
            setError(err instanceof Error ? err : new Error('Failed to get location'));
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Refetch function that ignores cache
    const refetch = useCallback(() => {
        return fetchLocation(true);
    }, [fetchLocation]);

    useEffect(() => {
        fetchLocation();
    }, [fetchLocation]);

    return { location, loading, error, refetch };
}
